import {
  type ChangeEvent,
  type FC,
  type KeyboardEvent,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { twMerge } from "tailwind-merge";

import { HoverableToolbarContext } from "./HoverableToolbarProvider";

import { TextArea } from "mds/components/TextArea";
import { TextField, type TextFieldSize } from "mds/components/TextField";
import { useOutsideClick } from "mds/hooks/use-outside-click";
import { useAppSelector } from "store/index";
import { selectIsEditModeEnabled } from "store/selectors";

type EditableTextSize = Extract<TextFieldSize, "s" | "m" | "h5" | "h4" | "h3" | "h2" | "h1">;

type EditableTextProps = {
  multiline?: boolean;
  isEditing?: boolean;
  shouldWrap?: boolean;
  className?: string;
  textSize?: EditableTextSize;
  value: string;
  label?: string;
  validatorFn?: (value: string) => string[];
  onValueChanged?: (newValue: string) => void;
  onSetIsEditing?: (editing: boolean) => void;
  onDoubleClick?: () => void;
};

// Prevent text from shifting vertically when toggling edit mode.
const SIZE_LAYOUT: Record<EditableTextSize, { display: string; edit: string }> = {
  s: {
    display:
      "body-s box-border flex h-[34px] min-h-[34px] items-center border-y border-transparent",
    edit: "box-border !h-[34px] !py-0",
  },
  m: {
    display:
      "body-m box-border flex h-[38px] min-h-[38px] items-center border-y border-transparent",
    edit: "box-border !h-[38px] !py-0",
  },
  h5: {
    display: "h5 box-border flex h-[30px] min-h-[30px] items-center border-y border-transparent",
    edit: "box-border !h-[30px] !py-0",
  },
  h4: {
    display: "h4 box-border flex h-[34px] min-h-[34px] items-center border-y border-transparent",
    edit: "box-border !h-[34px] !py-0",
  },
  h3: {
    display: "h3 box-border flex h-[38px] min-h-[38px] items-center border-y border-transparent",
    edit: "box-border !h-[38px] !py-0",
  },
  h2: {
    display: "h2 box-border flex h-[42px] min-h-[42px] items-center border-y border-transparent",
    edit: "box-border !h-[42px] !py-0",
  },
  h1: {
    display: "h1 box-border flex h-[50px] min-h-[50px] items-center border-y border-transparent",
    edit: "box-border !h-[50px] !py-0",
  },
};

const MULTILINE_DISPLAY_SIZE_CLASSES = {
  s: "body-s box-border min-h-[34px] border border-transparent px-2.5 py-2",
  m: "body-m box-border min-h-[38px] border border-transparent px-3 py-2.5",
} as const;

/**
 * A single piece of editable text, single-line or multiline.
 */
export const EditableText: FC<EditableTextProps> = ({
  value,
  shouldWrap = false,
  isEditing = false,
  className,
  label,
  multiline,
  textSize = "s",
  validatorFn,
  onSetIsEditing,
  onValueChanged,
  onDoubleClick,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const errorId = useId();
  const [draft, setDraft] = useState(value);
  const [prevValue, setPrevValue] = useState(value);

  // Keep draft in sync when the external value changes (e.g. another editor).
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  if (value !== prevValue) {
    setPrevValue(value);
    setDraft(value);
  }

  const validationErrors = validatorFn ? validatorFn(draft) : [];
  const hasValidationErrors = validationErrors.length > 0;
  const canDoubleClick = !!onDoubleClick;
  const validationErrorList = hasValidationErrors ? (
    <div className="body-xs mt-1 text-red" id={errorId}>
      {validationErrors.map((error) => (
        <div key={error}>{error}</div>
      ))}
    </div>
  ) : null;

  const save = useCallback(() => {
    if (!isEditing || hasValidationErrors) return;

    const trimmed = draft.trim();
    onSetIsEditing?.(false);

    // Single-line fields are titles — don't allow saving empty text.
    if (!multiline && !trimmed) {
      setDraft(value);
      return;
    }

    if (onValueChanged && trimmed !== value) {
      onValueChanged(trimmed);
    }
  }, [isEditing, hasValidationErrors, draft, multiline, onSetIsEditing, onValueChanged, value]);

  useOutsideClick(inputRef, save);

  const onTextFieldKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      save();
    }
  };

  const onTextFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDraft(event.target.value);
  };

  const onTextAreaChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setDraft(event.target.value);
  };

  if (multiline && isEditing) {
    return (
      <div className="flex w-full flex-col">
        <TextArea
          aria-describedby={hasValidationErrors ? errorId : undefined}
          aria-invalid={hasValidationErrors}
          aria-label={label}
          className={twMerge("w-full", className)}
          minRows={1}
          size={textSize === "m" ? "m" : "s"}
          value={draft}
          autoFocus
          onBlur={save}
          onChange={onTextAreaChange}
          onDoubleClick={onDoubleClick}
        />
        {validationErrorList}
      </div>
    );
  }

  if (multiline && !isEditing) {
    return (
      <div
        className={twMerge(
          "w-full whitespace-pre-wrap",
          MULTILINE_DISPLAY_SIZE_CLASSES[textSize === "m" ? "m" : "s"],
          canDoubleClick && "cursor-pointer",
          className,
        )}
        onDoubleClick={onDoubleClick}
      >
        {value}
      </div>
    );
  }

  if (!isEditing) {
    return (
      <div
        className={twMerge(
          "w-full min-w-0",
          SIZE_LAYOUT[textSize].display,
          // Wrapped titles need to grow past the single-line TextField height.
          shouldWrap && "!h-auto",
          canDoubleClick && "cursor-pointer",
          className,
        )}
        onDoubleClick={onDoubleClick}
      >
        <span className={twMerge("min-w-0", shouldWrap ? "w-full break-words" : "truncate")}>
          {value}
        </span>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col">
      <TextField
        aria-describedby={hasValidationErrors ? errorId : undefined}
        aria-invalid={hasValidationErrors}
        aria-label={label}
        className={twMerge("w-full", SIZE_LAYOUT[textSize].edit, className)}
        ref={inputRef}
        size={textSize}
        value={draft}
        autoFocus
        onChange={onTextFieldChange}
        onClick={(event) => {
          event.stopPropagation();
          event.preventDefault();
        }}
        onDoubleClick={onDoubleClick}
        onKeyDown={onTextFieldKeyDown}
      />
      {validationErrorList}
    </div>
  );
};

export type HoverableEditableTextProps = Omit<
  EditableTextProps,
  "isEditing" | "onSetIsEditing" | "onDoubleClick"
> & {
  disableUnlessEditMode?: boolean;
  editingOnStart?: boolean;
};

/*
 * This editable text works with the hoverable toolbar.
 * Originally the toolbar and the text were one component, which meant using the text
 * even when only the toolbar was needed, and passing pre/post elements to control layout.
 * Splitting them lets the text be styled without the toolbar owning the layout.
 * A context still shares editing state between the toolbar and this control.
 */
export const HoverableEditableText: FC<HoverableEditableTextProps> = ({
  disableUnlessEditMode = false,
  editingOnStart,
  ...props
}) => {
  const { isEditing, setIsEditing } = useContext(HoverableToolbarContext);
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);

  const onDoubleClick = () => {
    setIsEditing(true);
  };

  useEffect(() => {
    if (editingOnStart) {
      setIsEditing(true);
    }
  }, [editingOnStart, setIsEditing]);

  return (
    <EditableText
      {...props}
      isEditing={isEditing}
      onDoubleClick={isEditModeEnabled || !disableUnlessEditMode ? onDoubleClick : undefined}
      onSetIsEditing={setIsEditing}
    />
  );
};
